import type { SerializableNode, SerializerProps } from '@/serializer';
import type { ProductData, ProductVariantData } from '@/types';
import { currencyFormatter } from '@/utils/currencyFormatter';
// TODO: make this util generic
import { updateText } from '@/utils/post/utils';
import type { JSONContent } from '@tiptap/core';
import type { ReactNode } from 'react';
import { getElement } from '../../serializer/getElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf/index';

interface ExtendedSerializerProps extends SerializerProps {
  product: ProductData;
  productVariant: ProductVariantData;
  node: JSONContent & SerializableNode;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    product,
    productVariant,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  const isImage = node?.type === 'image';
  const isCheckoutButton = node?.type === 'button' && node?.attrs?.action === 'product_checkout';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';

  if (isCheckoutButton) {
    if (node?.attrs?.mergeTag === 'product.url') {
      node = {
        ...node,
        attrs: {
          ...node.attrs,
          url: product.url,
        },
      } as JSONContent & SerializableNode;
    }
  }

  // Swap out images
  if (isImage && node.attrs?.mergeTag) {
    if (node.attrs.mergeTag === 'product.image') {
      const imageUrl = product?.default_variant?.default_asset_url;
      node = {
        ...node,
        attrs: {
          ...node.attrs,
          alt: product?.name,
          src: imageUrl,
        },
      } as JSONContent & SerializableNode;
    }
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && node.attrs?.mergeTag) {
    if (node.attrs.mergeTag === 'product.name') {
      node = updateText(node, product?.name) as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'product.description') {
      node = updateText(node, product?.description || '') as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'product.cta_text') {
      node = updateText(node, product?.cta_text || '') as JSONContent & SerializableNode;
    }
    if (
      node?.attrs?.mergeTag === 'product.unit_amount' &&
      productVariant?.unit_amount_cents &&
      productVariant?.currency
    ) {
      node = updateText(
        node,
        currencyFormatter({ currency: productVariant.currency }).format(productVariant.unit_amount_cents / 100),
      ) as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  if (node?.type === 'button') {
    // This is to prevent hydration errors since this is nested in an anchor tag
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        renderAsDiv: true,
      },
    } as JSONContent & SerializableNode;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          product,
          productVariant,
        },
        [...path, i],
      ),
    ) || [];

  return getElement({
    node: node as SerializableNode,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
  });
};

function ProductSerializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}

export { ProductSerializer };
